<template>
    <div>
      
        <div class="date-picker-container" style="font-size: small;">
        <Datepicker 
            v-model="localStartDate" 
            :placeholder="$t('controls.datePicker.start')"
            :locale="dateLocale"
            :lower-limit="minDate"
            :upper-limit="maxDate"
            @update:modelValue="emitStartDate"
        ></Datepicker>
        <Datepicker 
            v-model="localEndDate" 
            :placeholder="$t('controls.datePicker.end')"
            :locale="dateLocale"
            :lower-limit="minDate"
            :upper-limit="maxDate"
            @update:modelValue="emitEndDate"
        ></Datepicker>
      </div>

    </div>
  </template>

<script>
import { computed, onBeforeMount, ref, watch, } from 'vue'
import { useI18n } from 'vue-i18n'
import { nl } from 'date-fns/locale'
import Datepicker from 'vue3-datepicker'
import { getLastUpdatedDataDate } from "@/api/eventsAPI.js"

export default {
    name: 'DatePicker',

    components: {
        Datepicker
    },

    props: {
        startDate: Date,
        endDate: Date,
        clearDatePicker: Boolean,
    },

    emits: ['updateStartDate', 'updateEndDate', 'updateDateRangeMode', 'updateClearDatePicker'],
    
    setup(props, { emit }) {
        const maxDate = ref(new Date())    // will become (lastUpdatedDate - 1d)
        const minDate = ref(new Date())    // will become (maxDate - 365d)
        const dateRangeMode = ref('')
        // Month and weekday names in the picker follow the portal language;
        // undefined keeps the picker's own English default.
        const { locale } = useI18n()
        const dateLocale = computed(() => (locale.value === 'nl' ? nl : undefined))

        // Override props with null to ensure the fields start empty
        const localStartDate = ref(null)
        const localEndDate = ref(null)
        const clearDatePicker = ref()

        // Apply the prop values only if they are not null
        if (props.startDate) {
            localStartDate.value = new Date(props.startDate)
        }
        if (props.endDate) {
            localEndDate.value = new Date(props.endDate)
        }

        watch(() => props.clearDatePicker, (newValue) => {
            if (newValue) {
                localStartDate.value = null
                emit('updateStartDate', localStartDate.value)
                localEndDate.value = null
                emit('updateEndDate', localEndDate.value)
                clearDatePicker.value = false
                emit('updateClearDatePicker', clearDatePicker.value)
            }
        })

         onBeforeMount(async () => {
            try {
                const iso = await getLastUpdatedDataDate()    // e.g. "2025-06-13T10:30:00+02:00"
                const lastUp = new Date(iso)

                // “upper-limit” is the day before that:
                const u = new Date(lastUp)
                u.setDate(u.getDate() - 1)
                maxDate.value = u

                // “lower-limit” is 365 days before the upper-limit:
                const l = new Date(u)
                l.setDate(l.getDate() - 365)
                minDate.value = l
            } catch (err) {
                console.error("couldn't fetch lastUpdatedDate:", err)
            }
        })

        const emitStartDate = (newDate) => {
            localStartDate.value = newDate
            emit('updateStartDate', localStartDate.value)
        }

        const emitEndDate = (newDate) => {
            localEndDate.value = newDate
            clearDatePicker.value = false
            dateRangeMode.value = 'dateRange'
            emit('updateDateRangeMode', dateRangeMode.value)
            emit('updateEndDate', localEndDate.value)
            emit('updateClearDatePicker', clearDatePicker.value)
        }


        return {
            localStartDate,
            localEndDate,
            minDate,
            maxDate,
            dateLocale,
            emitStartDate,
            emitEndDate,
        }
    }
}
</script>

<style scoped>

.date-picker-container {
    display: flex;
    justify-content: start;
    margin-top: 5px;
    margin-bottom: 5px;
    margin-right: 25px;
    gap: 10px;
    flex-wrap: wrap;
}

</style>